import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { cloneDeep } from "lodash-es";
import { DateTime } from "luxon";
import { articles, syncState } from "@/plugins/LocalForagePlugin";
import { articlesSyncKey } from "@/types/products/product-definitions";
import type {
    Article,
    ArticleMetaData,
    Author,
    Category,
    ContentfulArticle,
    HasMetaData,
    IndustrySector,
    InvestorsIntelligenceSector,
    Product,
    SearchableTicker,
    Tag
} from "@/types/entities";
import type { Articles } from "@/types/api/content-models";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";
import { CompositionContext } from "@/services/data/sync-supervisor";

export class ArticlesSyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise< number > {
        let itemsUpdated = 0;

        try {

            const syncThresholdInMillis = await syncState.getItem< number >( articlesSyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() )
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( articlesSyncKey, nextSyncThresholdInMillis );

                logger.debug( `[ articles-sync-coordinator ] load all available article meta-data...` );
                const t0: number = performance.now();

                // Retrieve & store the latest meta-data for articles (all - both public and subscription-based)
                const allMetadata = await this.refreshMetaDataFromServer( authToken );

                if(0 === allMetadata.length)
                {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.setItem( articlesSyncKey, nextSyncThresholdInMillis );

                    return itemsUpdated;
                }

                const t1: number = performance.now();
                logger.info( `[ articles-sync-coordinator ] Retrieved & loaded ${ allMetadata.length } article meta records in ${ t1 - t0 } ms` );

                const allMetaDataByIdMap = new Map< string, ArticleMetaData >(
                    allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
                );

                // Manage all cached subscription-based articles
                const t2: number = performance.now();

                if ( await articles.length() > 0 ) {
                // Iterate over all elements within IDB
                await articles.iterate( ( content: Article, key: string ) => {
                    // If the subscriptionArticles in IDB matches one in the meta-data repository
                    const meta: ArticleMetaData | undefined = allMetaDataByIdMap.get( key );
                    // If the meta-data collection has the same article content item that shares the same contentful id...
                    if ( typeof meta !== 'undefined' ) {
                        // If the updated date for both the meta-data and the local article are the same...
                        if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                            // Remove it from the list of available meta-data items
                            allMetaDataByIdMap.delete( key );
                        }
                        // Otherwise, the local content item is out of date and should be replaced
                        else {
                            // Delete the content item from the IDB collection
                            articles.removeItem( key );
                        }
                    }
                    // Else, the current meta-data no longer contains the same content item and thus it should be removed from IDB
                    else {
                        // Delete the content item from the IDB collection
                        articles.removeItem( key );
                    }

                    return;
                } );
            }

            const t3: number = performance.now();
            logger.info( `[ articles-sync-coordinator ] Aggregated missing subscription articles in ${ t3 - t2 } ms` );

            // Transform metadata map into an array of content ids
            const missingIds = [ ...Array.from( allMetaDataByIdMap.keys() ) ];
            // If any content is missing...
            if ( missingIds.length > 0 ) {
                // Fetch it from the server
                itemsUpdated = await this.refreshContentFromServer( authToken, missingIds );
            }

            const t4: number = performance.now();
            logger.info( `[ articles-sync-coordinator ] Processing all missing articles took ${ t4 - t0 } ms` );
        }
            
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
        }

        return itemsUpdated;
    }

    private async refreshMetaDataFromServer( authToken: string ): Promise< ArticleMetaData[] > {
        let page: number = 0;
        const pageSize: number = 5000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        const metadata: ArticleMetaData[] | undefined = [];
        let metaPayload: ContentfulArticle[] | undefined = [];

        do {
            const response =
                await goFetch< Articles, PersistedQueryPayload >( {
                    endpoint: 'content/graphql',
                    payload: {
                        id: '27f9b6993a8eb710c390171b82547d1d',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                fromPublishedDateTimeUTC: '2008-01-01'
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Article metadata fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping article metadata pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            metaPayload = response?.data?.articlesCatalog?.articles;
            if ( metaPayload ) {
                metaPayload.map( ( a: ContentfulArticle ) => {
                    metadata.push( ( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at
                    } ) );
                } );
            }
            // update the page #
            page++;
        } while ( (metaPayload?.length ?? 0) >= pageSize );

        return metadata;
    }

    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise< number > {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();
        let itemsUpdated = 0;

        let page: number = 0;
        const pageSize: number = 350;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        let contentPayload: ContentfulArticle[] | undefined = [];
        do {
            const response =
                await goFetch< Articles, PersistedQueryPayload >( {
                    endpoint: 'content/graphql',
                    payload: {
                        id: '1f2736d6d67c809d2304318cc1c20a74',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                ids: missingContentIds
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Article content fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping article content pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            contentPayload = response?.data?.articlesCatalog?.articles;
            if ( null != contentPayload ) {
                const allArticles: Article[] = [];
                contentPayload.map( ( a: ContentfulArticle ) => {
                    const article: Article | undefined = composeArticle( a, this.#compositionContext );
                    if ( typeof article !== 'undefined' ) {
                        allArticles.push( article );
                    }
                } );

                // Store the items recently retrieved
                await articles.ready();
                try {
                    for ( const origArticle of allArticles ) {
                        await articles.setItem( origArticle.contentfulId, cloneDeep( origArticle ) );
                    }
                } catch ( err: any ) {
                    logger.error( JSON.stringify( err.message ) );
                }

                const t1: number = performance.now();
                logger.info( `[ articles-sync-coordinator ] Processing ${ allArticles.length } articles took ${ t1 - t0 } ms` );

                itemsUpdated += allArticles.length;
            }

            // update the page #
            page++;
        } while ( (contentPayload?.length ?? 0) >= pageSize );

        return itemsUpdated;
    }
}

export function composeArticle( article: ContentfulArticle, ctx: CompositionContext ): Article {
    let hydratedArticle = {} as Article;
    try {
        // Author
        const author: Author | undefined = ctx.getAuthorById( article.author_id );

        // Ad Product
        const adProduct: Product | undefined = !!( article.ad_product_id ) ?
            ctx.getProductById( article.ad_product_id ) : undefined;

        // Categories
        const categories: Category[] = [];
        article.assoc_category_ids?.forEach( ( id: string ) => {
            const category: Category | undefined = ctx.getCategoryById( id );

            if ( category ) {
                categories.push( cloneDeep( category ) );
            }
        } );

        // Industry Sectors
        const industrySectors: IndustrySector[] = [];
        article.assoc_ind_sector_ids?.forEach( ( id: string ) => {
            const industrySector: IndustrySector | undefined = ctx.getIndustrySectorById( id );

            if ( industrySector ) {
                industrySectors.push( cloneDeep( industrySector ) );
            }
        } );

        // Products
        const products: Product[] = [];
        article.assoc_product_ids?.forEach( ( id: string ) => {
            const product: Product | undefined = ctx.getProductById( id );

            if ( product ) {
                products.push( cloneDeep( product ) );
            } else {
                logger.warn( `The given product id '${ id }' was not found to match a known Product content item.` );
            }
        } );

        // Sectors
        const sectors: InvestorsIntelligenceSector[] = [];
        article.assoc_sector_ids?.forEach( ( id: string ): void => {
            const sector: InvestorsIntelligenceSector | undefined = ctx.getSectorById( id );

            if ( sector ) {
                sectors.push( cloneDeep( sector ) );
            }
        } );

        // Tags
        const tags: Tag[] = [];
        article.assoc_tag_ids?.forEach( ( id: string ): void => {
            const tag: Tag | undefined = ctx.getTagById( id );

            if ( tag ) {
                tags.push( cloneDeep( tag ) );
            }
        } );

        // Tickers
        const tickers: SearchableTicker[] = [];
        article.assoc_ticker_ids?.forEach( ( id: string ): void => {
            const ticker: SearchableTicker | undefined = ctx.getTickerById( id );

            if ( ticker ) {
                tickers.push( cloneDeep( ticker ) );
            }
        } );

        hydratedArticle = ( {
            contentfulId: article.id,
            publishedDate: article.publish_dt,

            updatedDate: article.publish_at,

            slug: article.slug ?? undefined,
            title: article.title ?? undefined,
            text: article.body ?? undefined,
            body2: article.body2 ?? undefined,
            preview: article.preview ?? undefined,
            thumbnail: article.thumbnail_uri ?? undefined,

            adProduct: !!( adProduct ) ? cloneDeep( adProduct ) : undefined,
            editorialAd: article.editorial_ad ?? undefined,

            videoLink: null === article.video_id ? undefined : `https://vimeo.com/${ article.video_id }`,

            author: cloneDeep( author ),
            categories: categories,
            industrySectors: industrySectors,
            products: products,
            sectors: sectors,
            tags: tags,
            tickers: tickers
        } );
    } catch ( e: any ) {
        logger.error( JSON.stringify( e.message ) );
    }

    return hydratedArticle;
}
